import { Bot, Layers, User2, GitBranch } from 'lucide-react'
import type { OrchWorkflow } from '../types'
import { statusToOrchMap, type OrchStatus } from '@/shared/constants/workflow'

// ── Source parsing ─────────────────────────────────────────────────────────────

export type SourceKind = 'scanner' | 'pipeline' | 'manual' | 'sync'

export type ParsedSource = { label: string; kind: SourceKind; icon: typeof Bot }

export function parseWorkflowSource(promptId: string): ParsedSource {
  if (!promptId) return { label: 'Unknown', kind: 'manual', icon: User2 }
  if (promptId.startsWith('agent_')) {
    const name = promptId.replace('agent_', '')
    if (name === 'scan') return { label: 'Agent Scan', kind: 'scanner', icon: Bot }
    const display = name.charAt(0).toUpperCase() + name.slice(1)
    return { label: `${display} Scanner`, kind: 'scanner', icon: Bot }
  }
  if (promptId === 'user_created') return { label: 'Manual', kind: 'manual', icon: User2 }
  if (promptId.startsWith('pipeline_')) {
    const type = promptId.replace('pipeline_', '')
    return { label: `Pipeline · ${type}`, kind: 'pipeline', icon: Layers }
  }
  if (promptId.startsWith('improvement:')) return { label: 'Improvement Sync', kind: 'sync', icon: GitBranch }
  return { label: promptId, kind: 'manual', icon: User2 }
}

export const sourceKindColor: Record<SourceKind, string> = {
  scanner: 'text-blue-500',
  pipeline: 'text-purple-500',
  manual: 'text-zinc-500',
  sync: 'text-teal-500',
}

// ── Sorting ─────────────────────────────────────────────────────────────────

export function sortWorkflows(wfs: OrchWorkflow[], mode: 'priority' | 'newest'): OrchWorkflow[] {
  if (mode === 'newest') {
    return [...wfs].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }
  const priorityOrder: Record<OrchWorkflow['priority'], number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  }

  return [...wfs].sort((a, b) => {
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
    if (priorityDiff !== 0) {
      return priorityDiff
    }

    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })
}

// ── Status mapping (re-exported from shared constants) ───────────────────────

export const statusMap: Record<string, OrchStatus> = statusToOrchMap
